import { formatBytes, type TrainingExerciseDto, type TrainingPlanDto } from '@ukinfo/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import {
  fetchExercises,
  removeOffline,
  saveOffline,
  uniqueVideos,
  useOfflineAvailable,
  useOfflineEntries,
  useOnline,
  videoBytes,
  type OfflineMetaInput,
} from '../../lib/offline';
import { Button, Checkbox, Dialog, useOptionalToast } from '../ui';

/**
 * "Für offline speichern": legt die Einheit samt Übungen (Texte, Skizzen) auf diesem Gerät ab, damit sie
 * am Platz ohne Netz zu sehen und abzuspielen ist. Videos sind groß und kommen nur auf eigenen Wunsch mit -
 * die Größe steht vorher da.
 *
 * Der Knopf erscheint nur, wo es geht: mit Leserecht auf die Trainingsplanung, nicht in der Testansicht
 * und nur, wenn der Browser einen Speicher dafür hat.
 */

type Loaded =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; exercises: Record<string, TrainingExerciseDto> };

export function OfflineSaveButton({ plan, meta, className }: { plan: TrainingPlanDto; meta: OfflineMetaInput; className?: string }): JSX.Element | null {
  const { userId, available } = useOfflineAvailable();
  const online = useOnline();
  const queryClient = useQueryClient();
  const toast = useOptionalToast();
  const entries = useOfflineEntries(userId);
  const entry = entries.data?.find((candidate) => candidate.planId === plan.id);

  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState<Loaded>({ state: 'loading' });
  const [withVideos, setWithVideos] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Beim Öffnen die Übungen in voller Form holen (aus dem Zwischenspeicher der Abfragen, sonst vom Server).
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoaded({ state: 'loading' });
    setError(null);
    fetchExercises(plan, (id) =>
      queryClient.fetchQuery({ queryKey: ['training-exercise', id], queryFn: () => api.get<TrainingExerciseDto>(`/api/training/exercises/${id}`), staleTime: 60_000 }))
      .then((exercises) => { if (active) setLoaded({ state: 'ready', exercises }); })
      .catch((cause: unknown) => { if (active) setLoaded({ state: 'error', message: cause instanceof Error ? cause.message : 'Die Übungen ließen sich nicht laden' }); });
    return () => { active = false; };
  }, [open, plan, queryClient]);

  useEffect(() => {
    if (open) setWithVideos((entry?.videoIds.length ?? 0) > 0);
    // Nur beim Öffnen vorbelegen, nicht bei jeder Änderung des Eintrags.
  }, [open]);

  if (!available || !userId) return null;

  const exercises = loaded.state === 'ready' ? loaded.exercises : {};
  const videoCount = uniqueVideos(exercises).length;
  const bytes = videoBytes(exercises);

  const refreshList = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['offline-entries'] });

  const save = async (): Promise<void> => {
    if (loaded.state !== 'ready') return;
    setBusy(true);
    setError(null);
    try {
      await saveOffline({
        userId,
        plan,
        exercises: loaded.exercises,
        meta,
        videos: withVideos && videoCount > 0 ? 'with' : 'without',
        onProgress: (done, total) => setProgress({ done, total }),
      });
      await refreshList();
      toast?.show(withVideos && videoCount > 0 ? 'Gespeichert – mit Videos, auch ohne Netz da' : 'Gespeichert – auch ohne Netz da');
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const remove = async (): Promise<void> => {
    setBusy(true);
    try {
      await removeOffline(userId, plan.id);
      await refreshList();
      toast?.show('Vom Gerät entfernt');
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Entfernen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const hasVideosStored = (entry?.videoIds.length ?? 0) > 0;
  return (
    <>
      <Button type="button" variant="secondary" size="sm" className={className} onClick={() => setOpen(true)}>
        {entry ? 'Offline gespeichert' : 'Für offline speichern'}
      </Button>
      <Dialog
        open={open}
        onClose={() => { if (!busy) setOpen(false); }}
        title="Für offline speichern"
        footer={<Button variant="secondary" disabled={busy} onClick={() => setOpen(false)}>Schließen</Button>}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-700">
            „{meta.title}“ wird mit allen Übungen (Beschreibung und Skizze) auf diesem Gerät abgelegt – du kannst sie am Platz
            ohne Netz ansehen und abspielen. Du findest sie unter „Offline gespeichert“.
          </p>
          {entry && (
            <p className="rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              Gespeichert am {formatDateTime(entry.savedAt)}{entry.auto ? ' (automatisch)' : ''}
              {hasVideosStored ? ` · ${entry.videoIds.length} ${entry.videoIds.length === 1 ? 'Video' : 'Videos'} dabei` : ''}.
            </p>
          )}

          {loaded.state === 'loading' && <p className="text-sm text-slate-500">Übungen werden geladen …</p>}
          {loaded.state === 'error' && <p role="alert" className="text-sm text-rose-700">{loaded.message}</p>}
          {loaded.state === 'ready' && videoCount > 0 && (
            <div className="space-y-1">
              <Checkbox
                checked={withVideos}
                onChange={setWithVideos}
                disabled={busy}
                label={`Videos mitnehmen (${formatBytes(bytes)})`}
              />
              <p className="text-xs text-slate-500">
                {videoCount} {videoCount === 1 ? 'Video' : 'Videos'}. Videos sind groß – am besten im WLAN laden.
              </p>
            </div>
          )}
          {!online && <p role="status" className="text-sm text-amber-800">Zum Speichern brauchst du Netz.</p>}
          {progress && <p role="status" className="text-sm text-slate-600">Video {progress.done} von {progress.total} gespeichert …</p>}
          {error && <p role="alert" className="text-sm text-rose-700">{error}</p>}

          <div className="flex flex-wrap gap-2">
            <Button loading={busy} disabled={loaded.state !== 'ready' || !online} onClick={() => void save()}>
              {entry ? 'Aktualisieren' : 'Speichern'}
            </Button>
            {entry && (
              <Button variant="ghost" className="text-rose-700 hover:bg-rose-50 hover:text-rose-800" disabled={busy} onClick={() => void remove()}>
                Vom Gerät entfernen
              </Button>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
}
